import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import PhoneFrame from "@/components/PhoneFrame";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Shield, CheckCircle2, AlertCircle, Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function LegacyInvite() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [claim, setClaim] = useState(null);
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/legacy/invite/${token}`);
      setInfo(data);
      const pending = (data.pending_claims || [])[0];
      if (pending && pending.status === "pending") setClaim(pending);
    } catch (e) { setError(e?.response?.data?.detail || "Invite not found"); }
  };

  useEffect(() => { load(); }, [token]);

  const accept = async () => {
    setLoading(true);
    try { await axios.post(`${API}/legacy/invite/${token}/accept`); toast.success("Invite accepted"); load(); }
    catch { toast.error("Failed"); } finally { setLoading(false); }
  };

  const initiate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/legacy/invite/${token}/claim`);
      setClaim(data.claim);
      if (data.claim.status === "approved") { toast.success("Access granted!"); }
      else toast.info("Claim submitted — waiting for approvers");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const approve = async () => {
    if (!claim) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/legacy/invite/${token}/approve/${claim.id}`);
      setClaim(data.claim);
      if (data.claim.status === "approved") toast.success("Access granted!");
    } catch { toast.error("Approval failed"); } finally { setLoading(false); }
  };

  const loadItems = async (cat) => {
    if (!claim?.access_token) return;
    setLoading(true); setCategory(cat);
    try {
      const { data } = await axios.get(`${API}/legacy/access/${claim.access_token}/items`, { params: { category: cat } });
      setItems(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  if (error) {
    return (
      <PhoneFrame>
        <div className="px-6 pt-16 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
          <div className="font-display text-xl mt-4">Invite unavailable</div>
          <div className="text-sm text-muted-foreground mt-1">{error}</div>
        </div>
      </PhoneFrame>
    );
  }

  if (!info) return <PhoneFrame><div className="px-6 pt-16 text-center text-muted-foreground">Loading…</div></PhoneFrame>;

  return (
    <PhoneFrame>
      <div className="p-6 space-y-4">
        <div className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
          <Shield className="h-6 w-6" />
        </div>
        <div>
          <div className="font-display font-semibold text-2xl">Legacy invite</div>
          <p className="text-sm text-muted-foreground">{info.owner?.name || info.owner?.email} has nominated <b>{info.nominee.name}</b>{info.nominee.relation ? ` (${info.nominee.relation})` : ""} as a trusted person on Impo.</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4" data-testid="legacy-invite-card">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">You'll be able to access</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(info.nominee.allowed_categories || []).map((c) => <Badge key={c} className="rounded-full text-[10px]">{c}</Badge>)}
          </div>
          {!info.nominee.accepted && (
            <Button className="mt-4 w-full h-11" onClick={accept} disabled={loading} data-testid="legacy-accept-button">Accept invite</Button>
          )}
          {info.nominee.accepted && !claim && (
            <Button className="mt-4 w-full h-11" variant="outline" onClick={initiate} disabled={loading} data-testid="legacy-initiate-claim">
              <Lock className="h-4 w-4 mr-1.5" /> Initiate access claim
            </Button>
          )}
        </div>

        {claim && (
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="legacy-claim-card">
            <div className="flex items-center gap-2">
              <Badge className="rounded-full text-[10px] capitalize">{claim.status}</Badge>
              <div className="text-xs text-muted-foreground">Approvals: {(claim.approvals || []).length}</div>
            </div>
            {claim.status === "pending" && (
              <Button className="mt-3 w-full h-10" onClick={approve} disabled={loading} data-testid="legacy-approve-button">Approve claim</Button>
            )}
            {claim.status === "approved" && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 text-sm text-primary"><CheckCircle2 className="h-4 w-4" /> Access granted</div>
                <div className="grid grid-cols-3 gap-2">
                  {(info.nominee.allowed_categories || []).map((c) => (
                    <button key={c} onClick={() => loadItems(c)} className="rounded-xl border border-border bg-background p-2 text-xs" data-testid={`legacy-view-${c}`}>{c}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {items && (
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="legacy-items-card">
            <div className="text-sm font-medium capitalize">{category} — {items.length} items</div>
            <div className="mt-2 space-y-2 max-h-[50vh] overflow-y-auto">
              {items.map((it) => (
                <div key={it.id} className="rounded-xl border border-border p-2 text-xs">
                  <div className="font-medium">{it.title || it.label || it.text || "(untitled)"}</div>
                  <div className="text-muted-foreground line-clamp-2">{it.content || it.ocr_text || it.notes || it.caption || ""}</div>
                </div>
              ))}
              {items.length === 0 && <div className="text-xs text-muted-foreground">Nothing here.</div>}
            </div>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}
